import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Wrench, MapPin, Sparkles, PlusCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { CategoryGrid } from "@/components/CategoryGrid";
import { BusinessCard } from "@/components/BusinessCard";
import { Section } from "@/components/Section";
import { CardsSkeleton } from "@/components/CardsSkeleton";
import { CitySelector } from "@/components/CitySelector";
import { Button } from "@/components/ui/button";

const HERO = "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

export default function ServicesHub() {
  const params = useParams();
  const navigate = useNavigate();
  const city = params.city || localStorage.getItem("oyebest_city") || "etah";
  const [cityName, setCityName] = useState(localStorage.getItem("oyebest_city_name") || "Etah");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/service-city/${city}`)
      .then((r) => { setData(r.data); if (r.data?.city?.name) setCityName(r.data.city.name); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [city]);

  const cName = data?.city?.name || cityName;
  useSeo(`Local Services in ${cName} | OyeBest`, `Find trusted electricians, plumbers, AC mechanics, painters and more local service providers in ${cName} on OyeBest.`);

  const doSearch = (e) => { e.preventDefault(); navigate(`/search?q=${encodeURIComponent(q)}&city=${city}&tab=services`); };
  const comingSoon = data?.coming_soon;

  return (
    <>
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Local services" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/60 to-black/75" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-sm font-semibold text-white border border-white/20">
              <Wrench className="w-4 h-4 text-primary" /> Trusted Local Services
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
              Har Service, <span className="text-primary">Ek Call</span> Par.
            </h1>
            <p className="mt-5 text-lg text-white/85 max-w-2xl">
              Electrician, Plumber, AC Mechanic, Painter, Carpenter aur bahut kuch — bharosemand service providers apne shehar mein.
            </p>

            <form onSubmit={doSearch} className="mt-8 bg-white rounded-2xl p-2.5 flex flex-col sm:flex-row gap-2.5 shadow-2xl max-w-2xl">
              <div className="flex items-center flex-1 px-3">
                <Search className="w-5 h-5 text-muted-foreground shrink-0" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="AC mechanic, Electrician, Plumber…" className="w-full outline-none px-3 py-3 text-base" data-testid="services-hero-search-input" />
              </div>
              <CitySelector
                valueSlug={city}
                valueName={cName}
                onSelect={(slug, name) => { localStorage.setItem("oyebest_city", slug); localStorage.setItem("oyebest_city_name", name); navigate(`/services/${slug}`); }}
                triggerClassName="sm:w-44 h-12 rounded-xl border-border bg-white justify-between w-full"
              />
              <Button type="submit" className="h-12 rounded-xl px-8 font-semibold gap-2" data-testid="services-hero-search-btn">
                <Search className="w-4 h-4" /> Search
              </Button>
            </form>

            <div className="mt-4">
              <Button variant="outline" onClick={() => navigate("/list-service")} className="rounded-full bg-white/10 text-white border-white/30 hover:bg-white/20 gap-2 h-11" data-testid="hub-list-service-btn">
                <PlusCircle className="w-4 h-4" /> List Your Service — FREE
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Section title="Service Categories" subtitle={`Everything you need in ${cName}`}>
        {data && <CategoryGrid categories={data.categories} basePath={`/services/${city}`} />}
      </Section>

      {loading ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><CardsSkeleton /></div>
      ) : comingSoon ? (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center" data-testid="services-no-results">
          <div className="w-16 h-16 rounded-full bg-accent text-primary flex items-center justify-center mx-auto mb-4"><Wrench className="w-8 h-8" /></div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold">No service providers in {cName} yet</h2>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">Hum abhi {cName} mein service providers add kar rahe hain. Kya aap ek service dete hain? Sabse pehle list kijiye — bilkul FREE.</p>
          <Button onClick={() => navigate("/list-service")} className="mt-6 rounded-full gap-2 h-12 px-6 font-semibold" data-testid="services-empty-list-btn"><PlusCircle className="w-4 h-4" /> List Your Service</Button>
        </section>
      ) : (
        <>
          {data?.featured?.length > 0 && (
            <div className="bg-secondary/50">
              <Section title="Featured Services" subtitle="Top picks & premium providers">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {data.featured.map((b, i) => <BusinessCard key={b.id} business={b} index={i} />)}
                </div>
              </Section>
            </div>
          )}
          {data?.top_rated?.length > 0 && (
            <Section title={`Top Rated Services in ${cName}`} subtitle="Loved by the locals">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {data.top_rated.map((b, i) => <BusinessCard key={b.id} business={b} index={i} />)}
              </div>
            </Section>
          )}
        </>
      )}
    </>
  );
}
